import { reactive } from "vue";
import { getDatabase, ref as dbRef, onValue } from "firebase/database";
import { authStore } from "@/store/auth.js";

// Stripe writes entitlement asynchronously via the stripeWebhook handler, so on
// return from checkout we watch users/{uid}/billing until it reflects the
// purchased plan, with a timeout fallback. We match on lookupKey (not just
// status) so an existing "active" subscriber upgrading plans isn't reported as
// confirmed before the new plan actually lands.
const ENTITLEMENT_TIMEOUT_MS = 45000;
const ACTIVE_STATUSES = new Set(["active", "trialing"]);
// Persisted so a full-page Stripe redirect (web checkout) can rehydrate the
// expected plan on return — the reload wipes the in-memory pendingLookupKey.
const STORAGE_KEY = "al.pendingLookupKey";

// Shared singleton state: stripeUtils arms it, App.vue's overlay reads it.
export const billingConfirmation = reactive({
  phase: "idle", // "idle" | "finalizing" | "confirmed" | "timeout"
});

let unsubscribe = null;
let timer = null;
let pendingLookupKey = null;

function readStoredLookupKey() {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      return window.sessionStorage.getItem(STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (private mode / SSR) — in-memory tracking still works
    // for the native and same-page web paths.
  }
  return null;
}

function writeStoredLookupKey(lookupKey) {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      if (lookupKey) window.sessionStorage.setItem(STORAGE_KEY, lookupKey);
      else window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* storage unavailable — ignore */
  }
}

// Record which plan the user is buying so the watcher can confirm that specific
// plan landed. Set when checkout starts; the watch (which may begin later, on
// app-link return or after a web redirect) reads it.
export function armPendingCheckout(lookupKey) {
  pendingLookupKey = lookupKey || null;
  writeStoredLookupKey(pendingLookupKey);
}

// True when a checkout was armed and persisted but not yet confirmed. Used by
// the web checkout-return handler, which runs after a full-page reload has
// wiped the in-memory pendingLookupKey.
export function hasArmedCheckout() {
  return readStoredLookupKey() != null;
}

// True when a checkout was started (armed) but no watch has begun or
// finished. App.vue's appStateChange listener uses this so returning to the
// app without the app link firing (e.g. the user pays in the browser and
// switches back manually) still starts the entitlement watch, without
// double-starting it when the app link already did.
export function hasPendingCheckout() {
  return pendingLookupKey != null && billingConfirmation.phase === "idle";
}

export function startBillingWatch() {
  const uid = authStore.getUserId();
  if (!uid) return;

  // Rehydrate the expected plan when a full-page redirect wiped module state
  // (web return from Stripe) so an upgrade still waits for the *new* plan and
  // isn't confirmed on the already-active old one.
  if (pendingLookupKey == null) pendingLookupKey = readStoredLookupKey();
  const expectedLookupKey = pendingLookupKey;

  // Fail closed: with no expected plan, planOk would accept ANY active
  // entitlement — which falsely confirms an upgrade against the still-active
  // old plan. No plan identity (from the caller's arm, memory, storage, or
  // the return link's ?plan=) → no watch, no overlay, no success claim; a
  // real payment still lands reactively via the webhook. Every legitimate
  // caller arms first, so this only triggers on degraded paths (terminated
  // app + old backend, crafted/malformed deep links).
  if (expectedLookupKey == null) {
    return;
  }

  stopBillingWatch();
  billingConfirmation.phase = "finalizing";
  const billingRef = dbRef(getDatabase(), `users/${uid}/billing`);
  unsubscribe = onValue(
    billingRef,
    (snapshot) => {
      const billing = snapshot.val() || {};
      const statusOk = ACTIVE_STATUSES.has(billing.status);
      // expectedLookupKey is non-null (fail-closed gate above), so a confirm
      // always asserts the specific purchased plan, never "any active plan".
      const planOk = billing.lookupKey === expectedLookupKey;
      if (statusOk && planOk) {
        billingConfirmation.phase = "confirmed";
        stopBillingWatch();
      }
    },
    (error) => {
      // Signed out mid-watch or rules denial — fail fast instead of spinning
      // until the timeout.
      console.error("billingConfirmation: RTDB read failed", error);
      billingConfirmation.phase = "timeout";
      onWatchExhausted();
    }
  );

  timer = setTimeout(() => {
    if (billingConfirmation.phase === "finalizing") {
      billingConfirmation.phase = "timeout";
    }
    onWatchExhausted();
  }, ENTITLEMENT_TIMEOUT_MS);
}

// A watch that ends without confirming (timeout / read error) drops only the
// *persisted* lookup key, so a page reload from ?checkoutSuccess=true falls
// back to the success modal instead of re-arming and looping finalizing →
// timeout. The in-memory key is kept so the overlay's "Check again" still
// targets the purchased plan rather than confirming on an already-active one.
function onWatchExhausted() {
  writeStoredLookupKey(null);
  stopBillingWatch();
}

export function stopBillingWatch() {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

export function resetBillingConfirmation() {
  stopBillingWatch();
  pendingLookupKey = null;
  writeStoredLookupKey(null);
  billingConfirmation.phase = "idle";
}
